import {
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  inject,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LayoutComponent } from '../../core/layout/layout.component';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { MatOptionModule } from '@angular/material/core';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { DepartmentsService } from '../../features/departments/services/departments.service';
import { TasksService } from '../../features/tasks/services/tasks.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ServiceManagementService } from '../../features/service-management/services/service-management.service';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { EmployeeService } from '../../features/employees/employee.service';
import { PermissionService } from '../../shared/services/permission.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { ProjectService } from '../../features/projects/services/projects.service';

@Component({
  selector: 'app-branch',
  standalone: true,
  imports: [
    LayoutComponent,
    AgGridAngular,
    MatButtonModule,
    MatDialogModule,
    CommonModule
  ],
  templateUrl: './services.component.html',
  styleUrls: ['./services.component.scss'],
})
export class ServiceComponent {
  public viewPortSize: boolean = false;
  public rowData: any[] = [];

  canAddService$ = this.permissionsService.hasPermission(
    ModuleIds.ServiceManagement,
    'Create'
  );
  canEditService$ = this.permissionsService.hasPermission(
    ModuleIds.ServiceManagement,
    'Edit'
  );
  canDeleteService$ = this.permissionsService.hasPermission(
    ModuleIds.ServiceManagement,
    'Delete'
  );
  canEditService: boolean = false;
  canDeleteService: boolean = false;

  public columnDefs: ColDef[] = [
    {
      field: 'taskName',
      headerName: 'Task Name',
      flex: this.viewPortSize ? 1 : 0,
    },
    {
      field: 'taskCode',
      headerName: 'Task Code',
      flex: this.viewPortSize ? 1 : 0,
    },
    {
      field: 'price',
      headerName: 'Price',
      flex: this.viewPortSize ? 1 : 0,
    },
    {
      field: 'estimatedTime',
      headerName: 'Estimated Time',
      flex: this.viewPortSize ? 1 : 0,
    },
    {
      field: 'action',
      headerName: 'Action',
      // flex: this.viewPortSize ? 1 : 0,
      cellRenderer: (params: any) => {
        const div = document.createElement('div');
        div.style.display = 'flex';
        div.style.alignItems = 'center';
        div.style.gap = '20px';

        const createButton = (iconSrc: string, onClick: () => void) => {
          const span = document.createElement('span');
          span.style.cursor = 'pointer';
          span.style.display = 'flex';
          span.innerHTML = `<img src="${iconSrc}"/>`;
          span.addEventListener('click', onClick);
          return span;
        };
        div.appendChild(
          createButton('assets/images/icons/icon-edit.svg', () =>
            this.openEditDialog(params?.data?.id)
          )
        );
        div.appendChild(
          createButton('assets/images/icons/icon-delete.svg', () =>
            this.onDeleteTask(params.data?.id)
          )
        );
        return div;
      },
      floatingFilter: false,
      filter: false,
    },
  ];
  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public paginationPageSizeSelector: number[] | boolean = [10];
  public themeClass: string = 'ag-theme-quartz';
  public loading: boolean = true;
  public error: any = null;

  private searchSubject = new Subject<string>();
  profileName: any;
  taskData: any;

  constructor(
    private serviceManagementService: ServiceManagementService,
    private toastr: ToastrService,
    private route: Router,
    private permissionsService: PermissionService
  ) {
    const loggedInUser: any = localStorage.getItem('loggedInUser');
    const userData = JSON.parse(loggedInUser);
    this.profileName = userData?.profileData?.profileName;
  }

  ngOnInit() {
    this.fetchServices();
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.fetchServices(searchTerm);
      });

      this.canEditService$.subscribe((canEdit) => {
        this.canEditService = canEdit;
      });
      this.canDeleteService$.subscribe((canDelete) => {
        this.canDeleteService = canDelete;
      });

    this.checkViewportSize();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.setAGColumn();
  }

  setAGColumn() {
    this.columnDefs = [
      {
        field: 'serviceName',
        headerName: 'Service Name',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'serviceCode',
        headerName: 'Service Code',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'action',
        headerName: 'Action',
        // flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          const div = document.createElement('div');
          div.style.display = 'flex';
          div.style.alignItems = 'center';
          div.style.gap = '20px';

          const createButton = (iconSrc: string, onClick: () => void) => {
            const span = document.createElement('span');
            span.style.cursor = 'pointer';
            span.style.display = 'flex';
            span.innerHTML = `<img src="${iconSrc}"/>`;
            span.addEventListener('click', onClick);
            return span;
          };

          if(this.canEditService) {
            div.appendChild(
              createButton('assets/images/icons/icon-edit.svg', () =>
                this.openEditDialog(params?.data?.id)
              )
            );
          }

          if(this.canDeleteService){
            div.appendChild(
              createButton('assets/images/icons/icon-delete.svg', () =>
                this.onDeleteTask(params.data?.id)
              )
            );
          }
          return div;
        },
        floatingFilter: false,
        filter: false,
      },
    ];
  }

  onDeleteTask(serviceId: number): void {
    const dialogRef = this.dialog.open(ConfirmDialog);

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.deleteTask(serviceId);
      }
    });
  }

  private deleteTask(serviceId: number): void {
    this.serviceManagementService.removeService(serviceId).subscribe({
      next: (resp) => {
        this.toastr.success('Service deleted successfully');
        this.fetchServices();
      },
      error: (error: any) => {
        this.toastr.error(error, 'Error');
        this.loading = false;
      },
    });
  }

  private fetchServices(search: string = '') {
    this.loading = true;
    this.serviceManagementService.fetchServices(search).subscribe({
      next: (services) => {
        this.rowData = services.map((service: any) => {
          return {
            id: service?.id || '--',
            serviceName: service?.serviceName || '--',
            serviceCode: service?.serviceCode || '--',
            description: service?.description || '--',
          };
        });

        this.loading = false;
      },
      error: (error) => {
        this.error = error;
        this.loading = false;
      },
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  readonly dialog = inject(MatDialog);

  openConfirm() {
    const dialogRef = this.dialog.open(ConfirmDialog);
  }

  openDialog() {
    this.route.navigate(['services/add-services']);
  }

  openEditDialog(serviceId: any) {
    this.route.navigate(['services/edit-services', serviceId]);
  }
}

const gridDiv = document.querySelector<HTMLElement>('#myGrid')!;

@Component({
  selector: 'add-branch-dialog',
  templateUrl: './add-services.html',
  styleUrls: ['./services.component.scss'],
  standalone: true,
  imports: [
    // MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatOptionModule,
    CommonModule,
    FormsModule,
    DragDropModule,
  ],
})
export class AddServiceComponent implements OnInit {
  @Output() branchAdded = new EventEmitter<any>();
  serviceForm!: FormGroup;
  itemArray: any[] = [];
  taskData: any;
  serviceData: any;
  loading: boolean = false;
  editIndex: number | null = null;
  serviceId: string | null;
  readonly dialog = inject(MatDialog);
  departmentData: any;
  employees: any;

  constructor(
    private serviceManagementService: ServiceManagementService,
    private toastr: ToastrService,
    private http: HttpClient,
    private fb: FormBuilder,
    private route: Router,
    private router: ActivatedRoute,
    private taskService: TasksService, 
    private employeeService: EmployeeService,
    private projectService: ProjectService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.fetchTasks();
    this.loadDepartmentData();
    this.router.paramMap.subscribe((params) => {
      this.serviceId = params.get('id');
      this.fetchServiceById(Number(this.serviceId));
    });
  }

  initForm(): void {
    this.serviceForm = this.fb.group({
      serviceName: [this.serviceData?.serviceName || '', Validators.required],
      serviceCode: [this.serviceData?.serviceCode || '', Validators.required],
      description: [this.serviceData?.description || '', Validators.required],
      departmentId: [this.serviceData?.departmentId || '', Validators.required],
      employeeId: [this.serviceData?.employeeId || '', Validators.required],
      taskName: [''],
    });
  }

  private fetchTasks(search: string = '') {
    this.taskService.fetchTasks(search).subscribe({
      next: (tasks: any) => {
        this.taskData = tasks;
      },
      error: (error: any) => {
        this.toastr.error(error, 'Error');
      },
    });
  }


  addChecklist(task: any) {
    const taskName = task?.name;
    const serviceId = task?.id;
  
    if (taskName && serviceId) {
      const isAlreadyAdded = this.itemArray.some(
        (item: any) => item?.id === serviceId || item?.taskId === serviceId
      );
  
      if (isAlreadyAdded) {
        this.toastr.warning('Please select a different task, it is already added', 'Warning');
        return;
      }
  
      if (this.editIndex !== null) {
        const updatedItem = {
          ...this.itemArray[this.editIndex],
          taskName,
          taskId: serviceId,
        };
        delete updatedItem.__typename;
        this.itemArray[this.editIndex] = updatedItem;
        this.editIndex = null;
      } else {
        const newItem = {
          taskName,
          taskId: serviceId,
          isMandatory: false,
        };
        this.itemArray.push(newItem);  // Use push, not spread operator
      }
  
      this.serviceForm.get('taskName')?.reset();
    } else {
      this.toastr.warning('Please select a task', 'Warning');
    }
  }

  private loadDepartmentData() {
    this.employeeService.getDepartments().subscribe({
        next: (dept) => {
            this.departmentData = dept;
        },
        error: (error) => console.error(error)
    });
}

onDepartmentChange(event: MatSelectChange) {
  const employee = event.value;
  const departmentId = employee
  this.loadEmployeeTypeData(departmentId);
}

loadEmployeeTypeData(departmentId?: any) {
  this.projectService.getAllEmployees('', Number(departmentId)).subscribe({

    next: (employeeTypes) => {
      this.employees = employeeTypes;
    },
    error: (error) => console.error('Error fetching employee types:', error),
  });
}

  
  fetchServiceById(id: any) {
    if (id) {
      this.serviceManagementService.fetchServiceById(id).subscribe({
        next: (tasks) => {
          this.serviceData = tasks;
          this.initForm();
          this.itemArray =
            tasks?.serviceTasks?.map((task: any, i: number) => ({
              id: task?.id,
              taskName: task.taskData?.taskName,
              taskId: task?.taskId,
              isMandatory: task.isMandatory ?? false,
              sequenceOrder: String(task.sequenceOrder) || i,
            })) || [];
        },
        error: (error) => {
          this.toastr.error(error, 'Error');
        },
      });
    }
  }

  onEditItem(id: any) {
    const item = this.itemArray[id];
    this.serviceForm.get('taskName')?.setValue(item?.taskName);
    this.editIndex = id;
  }

  onRemoveItem(id: number) {
    if (this.serviceData?.id && this.itemArray[id]?.id) {
      const dialogRef = this.dialog.open(ConfirmDialog);

      dialogRef.afterClosed().subscribe((result: any) => {
        if (result === true) {
          this.serviceManagementService
            .removeServiceTask(this.itemArray[id]?.id)
            .subscribe({
              next: () => {
                this.toastr.success('Item removed successfully!', 'Success');
                this.itemArray = this.itemArray
                  .filter((_, index) => index !== id)
                  .map(({ __typename, ...rest }) => rest);
              },
              error: (error) => {
                this.toastr.error(error, 'Error');
                this.loading = false;
              },
            });
        }
      });
    } else {
      // New item, remove safely
      this.itemArray = this.itemArray.filter((_, index) => index !== id);
    }

    // Reset form if the removed item was being edited
    if (this.editIndex === id) {
      this.serviceForm.get('taskName')?.reset();
      this.editIndex = null;
    }
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
  

  dropItem(event: CdkDragDrop<any[]>) {
    console.log('this.itemArray',this.itemArray);
    console.log('event',event);
    
    
    moveItemInArray(this.itemArray, event.previousIndex, event.currentIndex);
  }

  onAddTask(): void {
    if (this.serviceForm.invalid) {
      this.serviceForm.markAllAsTouched();
      return;
    }

    console.log('this.itemArray',this.itemArray);
    

    if (this.itemArray.length === 0) {
      this.toastr.warning('Please add at least one task', 'Warning');
      return;
    }

    if (!this.serviceData) {
      this.loading = true;
      const createServiceInput = {
        serviceName: this.serviceForm.value.serviceName,
        serviceCode: this.serviceForm.value.serviceCode,
        description: this.serviceForm.value.description,
        departmentId: this.serviceForm.value.departmentId,
        employeeId: this.serviceForm.value.employeeId,
      };
      const serviceTaskItems = this.itemArray.map((item, index) => ({
        taskId: item.taskId,
        isMandatory: !!item.isMandatory,
        sequenceOrder: String(index + 1),
      }));

      this.serviceManagementService
        .createService(createServiceInput, serviceTaskItems)
        .subscribe(
          (response) => {
            this.loading = false;
            this.toastr.success('Service added successfully!', 'Success');
            this.route.navigate(['/services']);
          },
          (error) => {
            this.loading = false;
            let errorMessage = 'Failed to add service. Please try again.';

            this.toastr.error(errorMessage, 'Error');
          }
        );
    } else {
      if (this.serviceForm.valid) {
        this.loading = true;
        const updateTaskInput = {
          id: Number(this.serviceData?.id),
          serviceName: this.serviceForm.value.serviceName,
          serviceCode: this.serviceForm.value.serviceCode,
          description: this.serviceForm.value.description,
          departmentId: this.serviceForm.value.departmentId,
          employeeId: this.serviceForm.value.employeeId,
        };
        const taskCheckListItems = this.itemArray.map(({ __typename, taskName, ...rest }, index) => ({
          ...rest,
          sequenceOrder: String(index + 1),
          isMandatory: !!rest.isMandatory, // ensure it's a boolean
        }));        
        
        console.log('taskCheckListItems', taskCheckListItems);

        this.serviceManagementService
          .updateService(
            Number(this.serviceData?.id),
            updateTaskInput,
            taskCheckListItems
          )
          .subscribe(
            (response) => {
              this.loading = false;
              this.toastr.success('Service updated successfully!', 'Success');
              this.route.navigate(['/services']);
            },
            (error) => {
              this.loading = false;
              let errorMessage = 'Failed to update service. Please try again.';
              this.toastr.error(errorMessage, 'Error');
            }
          );
      }
    }
  }
}
